import { IconButton } from '@mui/material';
import Icon from '@mdi/react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../redux/store';
import ContextMenu from '../ContextMenu/ContextMenu';
import ContextMenuItem from '../ContextMenu/ContextMenuItem';
import { useParams } from 'react-router-dom';
import { vars } from '../../../Theme';
import { getFileTypeOrDefault } from '../../../util/FileUtils';
import { devOnClick, docTableActionsCallbacks, type ActionName } from './DocTableActions';
import {
    getActionGroups,
    getPermittedActions,
    getActionsListGroupByActionGroup,
} from '../../../redux/project/sliceProjectActions';
import { SelectedDocumentsMenuButton } from '../../Views/ProjectView/SelectedDocumentsMenuButton';
import dayjs from 'dayjs';
import { IDocDefaultFields } from '../../../redux/project/documents/sliceProjectDocuments';

/**
 * Component for a dynamic context menu for a document in a project database.
 */
export default (props: IDocTableMenuProps) => {
    /** Get relevant values from Redux store. */
    const docTypes = useSelector((store: IAppReduxStore) => store.projectDocTypes.data);
    const actionsList = useSelector(getPermittedActions);
    const actionsGroupsList = useSelector(getActionGroups);
    const documentDetailsData = useSelector(
        (store: IAppReduxStore) => store.projectDocumentDetails.data,
    );
    const fileTypes = useSelector((store: IAppReduxStore) => store.projectFileTypes.data);
    const fallback = useSelector((store: IAppReduxStore) => store.projectFileTypes.default);
    const pendingActions = useSelector((store: IAppReduxStore) => store.actionHandling.actions);

    /** Get the replicaId from the react router. */
    const { replicaId } = useParams();

    /** Configure component translation. */
    const { i18n, t } = useTranslation('translations', { keyPrefix: 'docTable' });

    /** State slice for controlling the position of the button's menu. */
    const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);

    /** Deconstruct props. */
    const { docType, contextMenuPos, setContextMenu, icon, exclude, universalId, currentDocument } =
        props;

    /** Get the configuration for the provided DocType. */
    const docTypeConfig = docTypes?.find(x => x.name === docType);

    const document = useMemo(
        () => currentDocument ?? documentDetailsData,
        [currentDocument, documentDetailsData],
    );

    /* Gets file type and its properties. **/
    const documentFileType = useMemo(
        () => getFileTypeOrDefault(document.Files?.FileName),
        [document.Files?.FileName, fileTypes, fallback],
    );

    /** Filters object of the table's actions in accordance with exclude param. */
    const filteredDocTypeConfig = useMemo(() => {
        if (!exclude) return docTypeConfig;

        return {
            ...docTypeConfig,
            contextMenu:
                docTypeConfig?.contextMenu?.filter(item => !exclude.includes(item as ActionName)) ??
                [],
        };
    }, [exclude, docTypeConfig]);

    /**
     * Closes the context menu.
     */
    const handleCloseMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        setAnchorEl(null);
        if (contextMenuPos) {
            setContextMenu(null);
        }
    };

    /**
     * Makes the context menu visible.
     */
    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();

        setAnchorEl(e.target as HTMLElement);
    };

    const availableMenuActionsListGroupByActionGroup = useMemo(() => {
        if (!filteredDocTypeConfig?.contextMenu || !actionsList) {
            return [];
        }

        const availableMenuActionsList = filteredDocTypeConfig?.contextMenu
            .map(action => actionsList.find(item => item.name === action))
            .filter(Boolean);

        if (!actionsGroupsList) return availableMenuActionsList;
        return getActionsListGroupByActionGroup(availableMenuActionsList, actionsGroupsList);
    }, [filteredDocTypeConfig?.contextMenu, actionsList, actionsGroupsList]);

    const menuItems = useMemo(
        () =>
            availableMenuActionsListGroupByActionGroup
                /** Remap menu items from the configuration object. */
                .map(({ name, icon, color, translations, groupActions, extendedConfig }) => {
                    /** If actionConfig doesn't exist or the file type is unknown, null gets returned. */
                    if (name === 'view' && !documentFileType?.extensions?.length) {
                        return null;
                    }

                    if (groupActions?.length) {
                        return (
                            <SelectedDocumentsMenuButton
                                key={name}
                                actions={groupActions}
                                icon={icon ?? ''}
                                text={translations[i18n.language] ?? name}
                                color={color ?? vars.font}
                                replicaId={replicaId}
                                universalId={document.UniversalID ?? universalId}
                                idDocIndex={document.IdDocIndex}
                                fileName={document.Files?.FileName}
                                isFromKebab={true}
                                document={document}
                            />
                        );
                    }

                    return (
                        <ContextMenuItem
                            disabled={(pendingActions ?? []).includes(name)}
                            key={name}
                            text={translations[i18n.language]}
                            icon={icon}
                            onClick={() => {
                                (extendedConfig?.dynamicAction
                                    ? docTableActionsCallbacks.dynamicAction
                                    : docTableActionsCallbacks[name] ?? devOnClick)({
                                    replicaId,
                                    universalId: document.UniversalID ?? universalId,
                                    idDocIndex: document.IdDocIndex,
                                    fileName: document.Files?.FileName,
                                    documentNumber: document?.DocNumber,
                                    protocolName: t('protocolName', {
                                        dateAndTime: dayjs().format('YYYYMMDDHHmmss'),
                                    }),
                                    actionKey: name,
                                    document,
                                });
                            }}
                        />
                    );
                })
                /** Remove any null items from the array. */
                .filter(Boolean),
        [document, availableMenuActionsListGroupByActionGroup, pendingActions],
    );

    /** DocType configuration not found or document doesn't exist - stop here. */
    if (!filteredDocTypeConfig?.contextMenu?.length || !document || !actionsList) {
        return <></>;
    }

    /** If we have no menuItems to show in the menu from controls panel, we don't show the button. */
    if (!menuItems.length) {
        return null;
    }

    return (
        <>
            {icon ? (
                <IconButton onClick={handleClick} sx={{ color: vars.font }}>
                    <Icon path={icon} size={1} />
                </IconButton>
            ) : (
                <></>
            )}

            <ContextMenu
                anchorEl={anchorEl}
                open={!!anchorEl || !!contextMenuPos}
                onClose={handleCloseMenu}
                onClick={handleCloseMenu}
                anchorReference={contextMenuPos ? 'anchorPosition' : 'anchorEl'}
                anchorPosition={
                    contextMenuPos
                        ? { top: contextMenuPos?.mouseY, left: contextMenuPos?.mouseX }
                        : undefined
                }
                slotProps={{
                    root: {
                        onContextMenu: e => {
                            e.preventDefault();
                            handleCloseMenu(e);
                        },
                    },
                }}
            >
                {menuItems}
            </ContextMenu>
        </>
    );
};

/**
 * Interface that describes the props for a DocTableCell component.
 */
interface IDocTableMenuProps {
    /** Document's type. */
    docType: string;

    /** Document's UniversalId. */
    universalId: string;

    /** Position properties of the context menu. */
    contextMenuPos?: { mouseX: number; mouseY: number } | null;

    /** setContextMenu UseState for changing the state in the handleCloseMenu(). */
    setContextMenu?: React.Dispatch<
        React.SetStateAction<{
            mouseX: number;
            mouseY: number;
        }>
    >;

    /** Defines icon for the IconButton component. */
    icon?: string;

    /** Defines what items to exclude from the menu. */
    exclude?: ActionName[];

    currentDocument?: IDocDefaultFields;
}
